import React, { useEffect, useState } from 'react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'video.js/dist/video-js.css';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'; // Import Masonry

const GalleryMain = () => {
    const [gallery, setGallery] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('gallery');
    const [playingVideoId, setPlayingVideoId] = useState(null); 
    const [youtubeVideos, setYoutubeVideos] = useState([]);
    const [reels, setReels] = useState([]);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/gallery/`)
            .then((res) => {
                if (!res.ok) throw new Error('Network response was not ok');
                return res.json();
            })
            .then((data) => {
                setGallery(data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        fetch("https://admin.sunrisingcinevisionacademy.in/api/youtubevideos/")
            .then((res) => {
                if (!res.ok) throw new Error('Network response was not ok');
                return res.json();
            })
            .then((data) => setYoutubeVideos(data))
            .catch((err) => console.error(err));
    }, []);

    useEffect(() => {
        fetch("https://admin.sunrisingcinevisionacademy.in/api/reels")
            .then((res) => {
                if (!res.ok) throw new Error('Network response was not ok');
                return res.json();
            })
            .then((data) => setReels(data))
            .catch((err) => console.error(err));
    }, []);

    const videoData = {
        reels,
        youtubeVideos: youtubeVideos.map((video) => ({
            id: video.id,
            videoId: video.video_id, // Use video_id from backend
        })),
    };

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
    };

    const handleVideoClick = (videoId) => {
        setPlayingVideoId(playingVideoId === videoId ? null : videoId); // Toggle play/pause
    };

    const handleShortClick = (shortId) => {
        setPlayingVideoId(playingVideoId === shortId ? null : shortId); // Toggle play/pause
    };

    return (
        <section className="min-h-screen py-12">
            <div className="container px-4 mx-auto">
                <h2 className="mb-10 text-4xl font-extrabold tracking-tight text-center text-gray-800">
                    Gallery
                </h2>
                <div className="flex justify-center mb-8">
                    <button
                        className={`px-6 py-2 mx-2 rounded-lg font-medium ${
                            activeTab === 'gallery'
                                ? 'bg-orange-900 text-white shadow-md'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        onClick={() => setActiveTab('gallery')}
                    >
                       Image Gallery
                    </button>
                    <button
                        className={`px-6 py-2 mx-2 rounded-lg font-medium ${
                            activeTab === 'video'
                                ? 'bg-orange-900 text-white shadow-md'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        onClick={() => setActiveTab('video')}
                    >
                        Video Gallery
                    </button>
                </div>

                {activeTab === 'gallery' && (
                    <>
                        {loading && <div className="text-lg font-semibold text-center">Loading...</div>}
                        {error && <div className="font-medium text-center text-red-500">{error}</div>}
                        {!loading && !error && (
                            <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}>
                                <Masonry gutter="16px">
                                    {gallery.map((item) => (
                                        <img
                                            key={item.id}
                                            src={item.image}
                                            alt={item.Alt}
                                            className="object-cover w-full rounded-lg"
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                                height: 'auto',
                                            }}
                                        />
                                    ))}
                                </Masonry>
                            </ResponsiveMasonry>
                        )}
                    </>
                )}

                {activeTab === 'video' && (
                    <div>
                        <h3 className="mb-6 text-2xl font-bold text-gray-800">Shorts</h3>
                        <div className="flex space-x-6 overflow-x-auto scrollbar-hide">
                            {videoData.reels.map((reel) => (
                                <div
                                    key={reel.id}
                                    className="flex-shrink-0 w-56 overflow-hidden rounded-lg h-96" // Added rounded corners
                                >
                                    {playingVideoId === reel.id ? (
                                        <iframe
                                            src={reel.embed_url.replace('/shorts/', '/embed/') + '?controls=0&autoplay=1'}
                                            className="w-full h-full"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    ) : (
                                        <div
                                            className="w-full h-full cursor-pointer"
                                            onClick={() => handleShortClick(reel.id)} // Handle short click
                                        >
                                            <iframe
                                                src={reel.embed_url.replace('/shorts/', '/embed/') + '?controls=0'}
                                                className="w-full h-full"
                                                frameBorder="0"
                                                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <h3 className="mt-12 mb-6 text-2xl font-bold text-gray-800">YouTube Videos</h3>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {videoData.youtubeVideos.map((video) => (
                                <div
                                    key={video.id}
                                    className="relative overflow-hidden rounded-lg"
                                    style={{
                                        width: '100%', // Adjusted for grid responsiveness
                                        height: '200px', // 16:9 aspect ratio
                                    }}
                                >
                                    {playingVideoId === video.videoId ? (
                                        <iframe
                                            src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`} // Correct embed URL
                                            title="YouTube video player"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="w-full h-full"
                                        ></iframe>
                                    ) : (
                                        <>
                                            <img
                                                src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`} // YouTube thumbnail
                                                alt="YouTube Thumbnail"
                                                className="object-cover w-full h-full cursor-pointer"
                                                onClick={() => handleVideoClick(video.videoId)} // Handle video click
                                            />
                                            <div
                                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 cursor-pointer"
                                                onClick={() => handleVideoClick(video.videoId)} // Handle video click
                                            >
<svg height="70px" width="70px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 461.001 461.001" xmlSpace="preserve">
  <g>
    <path style={{ fill: '#F61C0D' }} d="M365.257,67.393H95.744C42.866,67.393,0,110.259,0,163.137v134.728 c0,52.878,42.866,95.744,95.744,95.744h269.513c52.878,0,95.744-42.866,95.744-95.744V163.137 C461.001,110.259,418.135,67.393,365.257,67.393z M300.506,237.056l-126.06,60.123c-3.359,1.602-7.239-0.847-7.239-4.568V168.607 c0-3.774,3.982-6.22,7.348-4.514l126.06,63.881C304.363,229.873,304.298,235.248,300.506,237.056z"/>
  </g>
</svg>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default GalleryMain;
